import classNames from "classnames";
import React, { Fragment, FunctionComponent } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { GreyButton, SecondaryButton } from "Shared/Buttons";
import styled, { css } from "styled-components";
import Car from "src/components/icons/Car";
import useSelector from "src/utils/useSelector";
import { VehicleState } from "Types/state";
import StepCard from "../StepCard";
import { anyUpgradesSelected } from "./lib";
import "./StepTwo.scss";

const StyledButton = css`
    margin-top: 18px;
    width: calc(100% - 46px) !important;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: auto !important;
    }
`;

export const PrimaryAction = styled(SecondaryButton)`
    ${StyledButton};
`;

export const SecondaryAction = styled(GreyButton)`
    ${StyledButton};
`;

export interface StepTwoProps {
    active?: boolean;
    className?: string;
    done?: boolean;
    onEditUpgrades: () => void;
    onExploreUpgrades: () => void;
    vehicle: VehicleState;
}

const StepTwo: FunctionComponent<StepTwoProps & WithTranslation> = ({
    active,
    className,
    done,
    onEditUpgrades,
    onExploreUpgrades,
    vehicle,
    t,
}) => {
    const upgradesLabel = useSelector((state) => state.account.account_disclaimers.upgrades_label);
    const hasUpgradesSelected = anyUpgradesSelected(
        vehicle.accessories ? vehicle.accessories.map((m) => m.id) : []
        // vehicle.services,
        // vehicle.insurance
    );
    const doneDone = hasUpgradesSelected || done;

    const renderUpgradesButton = (label: string) => {
        return hasUpgradesSelected ? (
            <SecondaryAction onClick={onExploreUpgrades}>
                {t("ContinueDeal.StepTwo.editUpgradesButtonLabel", { label })}
            </SecondaryAction>
        ) : (
            <PrimaryAction onClick={onExploreUpgrades}>
                {t("ContinueDeal.StepTwo.exploreUpgradesButtonLabel", { label })}
            </PrimaryAction>
        );
    };

    const renderContent = (label: string) => {
        let content;
        const { title } = vehicle;

        if (doneDone) {
            content = (
                <Fragment>
                    <p className="StepTwo__title">{t("ContinueDeal.StepTwo.doneTitle")}</p>
                    <p className="StepTwo__text">
                        {t("ContinueDeal.StepTwo.doneDescription", { label: label.toLowerCase() })}
                    </p>
                    {renderUpgradesButton(label)}
                </Fragment>
            );
        } else if (active) {
            content = (
                <Fragment>
                    <p className="StepTwo__title">{t("ContinueDeal.StepTwo.activeTitle")}</p>
                    <p className="StepTwo__text">
                        {t("ContinueDeal.StepTwo.activeDescription", { label: label.toLowerCase(), title })}
                    </p>
                    {renderUpgradesButton(label)}
                </Fragment>
            );
        } else {
            content = (
                <Fragment>
                    <p className="StepTwo__title">{t("ContinueDeal.StepTwo.title")}</p>
                    <p className="StepTwo__text">
                        {t("ContinueDeal.StepTwo.description", { label: label.toLowerCase(), title })}
                    </p>
                </Fragment>
            );
        }

        return content;
    };

    return (
        <StepCard active={active} className={classNames("StepTwo", className)} done={doneDone} icon={Car}>
            <span className="StepTwo__step-name">{t("ContinueDeal.StepTwo.name")}</span>

            {renderContent(upgradesLabel)}
        </StepCard>
    );
};

export default withTranslation()(StepTwo);
